import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, Alert, TextField, Checkbox, FormControlLabel, Chip,
  Box, Tooltip, CircularProgress, useMediaQuery, useTheme
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { getEmployeePayRecords, sendEmployeePayRecords } from '../../api';

const EMAIL_RE = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]{1,64}@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/;
const fmtDay = (s) => (s ? new Date(`${s}T12:00:00`).toLocaleDateString() : '');
const W2_STATUS = { not_generated: 'Will be generated now', generated: 'Generated', filed: 'Filed' };

export default function SendPayRecordsDialog({ open, onClose, person, onSent }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [to, setTo] = useState('');
  const [stubIds, setStubIds] = useState([]);
  const [years, setYears] = useState([]);
  const [consent, setConsent] = useState(false);
  const [note, setNote] = useState('');
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [requestedOn, setRequestedOn] = useState('');
  const [sentWarnings, setSentWarnings] = useState(null); // set after a send that carries warnings

  useEffect(() => {
    if (!open || !person) return undefined;
    let cancelled = false;
    setData(null); setError(''); setTo(''); setStubIds([]); setYears([]); setConsent(false); setNote(''); setAddressConfirmed(false); setRequestedOn(''); setSentWarnings(null);
    setLoading(true);
    getEmployeePayRecords(person.id)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setTo(res.data.employee?.email || '');
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load pay records');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, person]);

  const toggle = (list, setList, v) =>
    setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const n = stubIds.length + years.length;
  const maxDocs = data?.limits?.max_documents || 25;
  const noteMax = data?.limits?.note_max || 1000;
  const emailValid = EMAIL_RE.test(to.trim());
  const recordEmail = (data?.employee?.email || '').trim().toLowerCase();
  const addressMismatch = !!data && to.trim().toLowerCase() !== recordEmail;
  const inactive = data?.employee?.is_active === false;
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const canSend = !loading && !sending && !!data && n > 0 && n <= maxDocs && emailValid
    && (years.length === 0 || consent) && (!addressMismatch || addressConfirmed);

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      const res = await sendEmployeePayRecords(person.id, {
        to: to.trim(),
        stub_ids: stubIds,
        w2_years: years,
        note: note || undefined,
        employee_requested_copy: years.length > 0 ? consent : undefined,
        address_confirmed: addressMismatch ? addressConfirmed : undefined,
        requested_on: requestedOn || undefined,
      });
      onSent && onSent(`Sent ${res.data.documents.length} document(s) to ${res.data.to}`);
      const warnings = res.data.warnings || [];
      if (warnings.length > 0) {
        // Keep the dialog open so the warning is read, not lost with the snackbar.
        setSentWarnings(warnings);
      } else {
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send pay records');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!open} onClose={onClose} fullScreen={isMobile} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Send pay records{person ? ` — ${person.first_name} ${person.last_name}` : ''}
        <Tooltip title="Emails the ticked PDFs as attachments through your company's email settings. The message itself carries no pay amounts or SSN. Every send is recorded in the Transaction Log. At most 5 sends per person per hour.">
          <InfoOutlined fontSize="small" color="action" />
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {sentWarnings && (
          <Alert severity="warning" icon={<InfoOutlined fontSize="inherit" />} sx={{ mb: 2 }}>
            Sent. {sentWarnings.map((w) => <div key={w}>{w}</div>)}
          </Alert>
        )}
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={28} /></Box>}
        {data && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              size="small" fullWidth type="email" label="To" value={to}
              onChange={(e) => { setTo(e.target.value); setAddressConfirmed(false); }}
              InputLabelProps={{ shrink: true }}
              error={!!to && !emailValid}
              helperText={`Starts as the email on their record. Enter a personal address if they asked for one.${inactive ? " If this is a company mailbox they no longer use, enter their personal address." : ""}`}
            />

            {addressMismatch && (
              <Box>
                <Alert severity="warning">
                  This address isn't the one on record for this person. Email-only requests for a W-2 are a common scam —
                  confirm the address with the employee by phone or in person before sending.
                </Alert>
                <FormControlLabel
                  control={<Checkbox size="small" checked={addressConfirmed} onChange={(e) => setAddressConfirmed(e.target.checked)} />}
                  label="I confirmed this address with the employee"
                />
              </Box>
            )}

            <TextField
              size="small" type="date" label="Requested on (optional)" value={requestedOn}
              onChange={(e) => setRequestedOn(e.target.value)}
              InputLabelProps={{ shrink: true }} inputProps={{ max: todayStr }}
              helperText="The date the employee asked. Recorded in the Transaction Log to track the 30-day W-2 and state pay-stub deadlines (CA: 21 days)."
            />

            <Box>
              <Typography variant="subtitle2">Pay stubs</Typography>
              {data.stubs.length === 0 && (
                <Typography variant="caption" color="text.secondary">No pay stubs for this person yet.</Typography>
              )}
              {data.stubs.map((s) => (
                <Box key={s.id}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={stubIds.includes(s.id)} disabled={!s.file_present}
                      onChange={() => toggle(stubIds, setStubIds, s.id)} />}
                    label={s.period_name || `${fmtDay(s.pay_period_start)} – ${fmtDay(s.pay_period_end)}`}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                    {s.file_present
                      ? `Pay day ${fmtDay(s.check_date)}`
                      : "PDF missing — regenerate the run's pay stubs on Payroll"}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box>
              <Typography variant="subtitle2">Form W-2</Typography>
              {!data.w2.allowed && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{data.w2.reason}</Typography>
              )}
              {data.w2.allowed && data.w2.years.length === 0 && (
                <Typography variant="caption" color="text.secondary">No processed payroll for this person, so no W-2 years.</Typography>
              )}
              {data.w2.allowed && data.w2.years.map((y) => (
                <Box key={y.year}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={years.includes(y.year)} disabled={!y.available}
                      onChange={() => toggle(years, setYears, y.year)} />}
                    label={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                      {y.year}
                      <Chip size="small" label={W2_STATUS[y.status] || y.status} />
                    </Box>}
                  />
                  {!y.available && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>{y.reason}</Typography>
                  )}
                  {y.available && y.stale && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                      Payroll changed since this was generated — it will be regenerated when you send.
                    </Typography>
                  )}
                  {y.available && y.year === data.w2.current_year && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                      Covers processed payroll only — send after their final paycheck is processed.
                    </Typography>
                  )}
                </Box>
              ))}
              {years.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={consent} onChange={(e) => setConsent(e.target.checked)} />}
                    label="The employee asked for a copy of their W-2 by email"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                    This PDF is a copy for their records, not the official Form W-2. You must still give them the W-2 on paper, by January 31 or within 30 days of a former employee's request or final paycheck, whichever is later.
                  </Typography>
                </Box>
              )}
            </Box>

            <TextField
              size="small" fullWidth multiline minRows={2} label="Note to include (optional)"
              value={note} onChange={(e) => setNote(e.target.value)}
              inputProps={{ maxLength: noteMax }} InputLabelProps={{ shrink: true }}
              helperText={`${note.length}/${noteMax}`}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
        <Typography variant="caption" color={n > maxDocs ? 'error' : 'text.secondary'} sx={{ mr: 'auto', ml: 1 }}>
          {n} document(s) selected{n > maxDocs ? ` — at most ${maxDocs} per email` : ''}
        </Typography>
        <Button onClick={onClose} size={isMobile ? 'large' : 'medium'}>{sentWarnings ? 'Close' : 'Cancel'}</Button>
        <Button variant="contained" onClick={handleSend} disabled={!canSend || !!sentWarnings} size={isMobile ? 'large' : 'medium'}>
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
